import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Box } from "@lib/Layout";
import { Button, Empty, Modal, Input, message, Tabs } from "antd";
import { AudioOutlined } from "@ant-design/icons";
import CustomTable from "@components/CustomTable/CustomTable";
import useFetchAllSchedule from "../../../api/useFetchAllSchedule";
import ScheduleColumns from "./ScheduleColumns";
import RecurringScheduleColumns from "./RecurringScheduleColumns";

const { confirm } = Modal;
const { Search } = Input;
const { TabPane } = Tabs;
const ScheduleTable = ({ onEdit }) => {
  const [refresh, setRefresh] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isDLModalOpen, setIsDLModalOpen] = useState(false);
  const [editableScheduleId, setEditableScheduleId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("1");
  const { scheduledData, scheduleLoading, scheduleError, recurringSchedules } =
    useFetchAllSchedule(refresh);
  const navigate = useNavigate();

  const handleSearch = (value) => {
    setSearchText(value.trim().toLowerCase());
  };

  const filteredData = (data) => {
    if (!data) return [];

    return data.filter((item) =>
      item.Schedulename.toLowerCase().includes(searchText)
    );
  };

  const handleSelectChange = (selectedKeys) => {
    if (
      editableScheduleId &&
      selectedKeys.length > 0 &&
      selectedKeys[0] !== editableScheduleId
    ) {
      message.error(
        "Only one schedule can be edited at a time. Deselect the current schedule to edit another."
      );
      return;
    }
    setSelectedRowKeys(selectedKeys);
    if (selectedKeys.length === 0) {
      setEditableScheduleId(null);
    }
  };

  const handleEdit = (record) => {
    console.log(record, "record");

    if (editableScheduleId && editableScheduleId !== record.Scheduleid) {
      message.error("Only one schedule can be edited at a time.");
      return;
    }

    if (!editableScheduleId && editableScheduleId !== record.Scheduleid) {
      setEditableScheduleId(record.Scheduleid);
      if (activeTab === "1") {
        localStorage.removeItem("editScheduleIdRecurring");
        localStorage.setItem("editScheduleIdOneTime", record.Scheduleid);
      } else {
        localStorage.removeItem("editScheduleIdOneTime");
        localStorage.setItem("editScheduleIdRecurring", record.Scheduleid);
      }
      if (onEdit) {
        onEdit();
      }
    }
  };

  const handleOnDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.error("Please select a schedule to delete.");
    } else if (selectedRowKeys.length > 1) {
      message.error(
        "You can only delete one record at a time. Please select only one schedule."
      );
    } else {
      setIsDLModalOpen(true);
    }
  };

  const confirmDelete = async () => {
    if (selectedRowKeys.length === 0) return;

    try {
      const token = localStorage.getItem("token");

      const deletePromises = selectedRowKeys.map(async (Scheduleid) => {
        const response = await fetch(
          `https://usfarmdata.org/fastapi/email_function/?operation=deleteschedule`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              Scheduleid: Scheduleid,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to delete schedule: ${Scheduleid}`);
        }

        return Scheduleid;
      });

      await Promise.all(deletePromises);

      message.success("Selected schedules deleted successfully.");
      setIsDLModalOpen(false);
      setSelectedRowKeys([]);
      setRefresh((prev) => !prev);
    } catch (error) {
      console.error("Error deleting schedules:", error);
      message.error("Failed to delete selected schedules.");
    }
  };

  const handleViewReport = (record) => {
    console.log(record);
    localStorage.setItem("reportID", record.Scheduleid);
    localStorage.setItem("reportName", record.Schedulename);

    navigate("/schedulereport");
  };
  const RecurringViewReport = (record) => {
    console.log(record);
    localStorage.setItem("reportID", record.Scheduleid);
    localStorage.setItem("reportName", record.Schedulename);

    navigate("/schedulereport");
  };

  const columns = ScheduleColumns(setRefresh, handleViewReport);
  const RecurringColumns = RecurringScheduleColumns(
    setRefresh,
    RecurringViewReport
  );

  return (
    <>
      {scheduleLoading ? (
        <div className="h-full flex flex-col gap-2 justify-center items-center">
          <div role="status">
            <svg
              aria-hidden="true"
              className="inline w-8 h-8 text-gray-200 animate-spin fill-green-500"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          </div>
          <span className="text-slate-600">Fetching Data... Please wait.</span>
        </div>
      ) : scheduleError ? (
        <div className="h-full items-center justify-center flex">
          <span className="text-lg text-red-500">{scheduleError}</span>
        </div>
      ) : (
        <Container>
          <Box className="p-3 border-t border-b">
            <div className="flex items-center gap-10">
              <div className="text-lg text-slate-700 font-semibold">
                History
              </div>
              <Search
                placeholder="Search Schedule Name"
                allowClear
                enterButton="Search"
                onSearch={handleSearch}
                style={{
                  width: 300,
                }}
              />
              {selectedRowKeys.length === 1 && (
                <Button
                  type="primary"
                  ghost
                  onClick={() =>
                    handleEdit(
                      scheduledData.find(
                        (item) => item.Scheduleid === selectedRowKeys[0]
                      ) ||
                        recurringSchedules.find(
                          (item) => item.Scheduleid === selectedRowKeys[0]
                        )
                    )
                  }
                >
                  Edit
                </Button>
              )}
              {selectedRowKeys.length > 0 && (
                <Button danger ghost onClick={handleOnDelete}>
                  Delete
                </Button>
              )}
            </div>
          </Box>
          <Box className="p-3">
            <Tabs defaultActiveKey="1" onChange={setActiveTab}>
              <TabPane tab="One-time Schedule" key="1">
                <CustomTable
                  columns={columns}
                  selectableRows={true}
                  rowKey="Scheduleid"
                  onSelectChange={handleSelectChange}
                  selectedRowKeys={selectedRowKeys}
                  data={filteredData(scheduledData)} // Use scheduledData for one-time schedules
                  needActionColumn={false}
                  heightOffset={450}
                />
              </TabPane>
              <TabPane tab="Recurring Schedule" key="2">
                <CustomTable
                  columns={RecurringColumns}
                  selectableRows={true}
                  rowKey="Scheduleid"
                  onSelectChange={handleSelectChange}
                  selectedRowKeys={selectedRowKeys}
                  data={filteredData(recurringSchedules)}
                  needActionColumn={false}
                  heightOffset={450}
                />
              </TabPane>
            </Tabs>
          </Box>
          <Modal
            title="Confirm Deletion"
            visible={isDLModalOpen}
            onOk={confirmDelete}
            onCancel={() => setIsDLModalOpen(false)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            Are you sure you want to delete the selected schedules?
          </Modal>
        </Container>
      )}
    </>
  );
};

export default ScheduleTable;
